import { useCallback, useEffect, useRef, useState } from 'react';
import type { MailClient } from '../api/client';
import { MailError, type Email, type Mailbox } from '../api/types';
import { compileSearch, DEFAULT_SORT, operatorFor, type Sort } from './search';

/*
    Windowed message list. The INBOX holds 2,909 messages and spam@ 1,512, so ids
    are fetched a page at a time as the virtualiser scrolls rather than up front,
    and headers only for ids we actually have. Bodies are never fetched here.
*/

const PAGE = 100;

export interface MailboxList {
    total: number;
    ids: (string | null)[];
    emails: Record<string, Email>;
    loading: boolean;
    /*
        Whether the server has answered for this folder yet. `loading` starts false and stays false
        until the first request goes out — which waits for the folders and, after sign-in, for the
        session's first IMAP connection, about two seconds — so "not loading and nothing in it"
        used to be true before anything had been asked, and the list said the folder was empty
        (measured 2026-09-24: 0 rows and "Bu klasörde bir şey yok" at 1.5 s, 8 rows at 4 s).
    */
    loaded: boolean;
    error: string | null;
    /** Parts of the query this server can't answer, so nobody assumes they did. */
    ignored: string[];
    ensureRange: (start: number, end: number) => void;
    reload: () => void;
    patch: (id: string, changes: Partial<Email>) => void;
    drop: (id: string) => void;
}

/**
 * Drops the conditions a server rejects and says which.
 *
 * This app ships to whatever mail server someone runs, and JMAP lets a server
 * refuse any filter it hasn't implemented — Stalwart has no `attachmentName`,
 * for one. Failing the whole search over one unsupported term would make
 * `filename:` look like a bug in the search box; dropping it silently would be
 * worse still, because the results would look complete.
 */
function without(filter: Record<string, unknown>, property: string): Record<string, unknown> | null {
    if (Array.isArray(filter.conditions)) {
        const kept = (filter.conditions as Record<string, unknown>[])
            .map((c) => without(c, property))
            .filter(Boolean) as Record<string, unknown>[];
        if (!kept.length) return null;
        return kept.length === 1 ? kept[0]! : { ...filter, conditions: kept };
    }
    return property in filter ? null : filter;
}

export function useMailboxList(
    client: MailClient | undefined,
    accountId: string | undefined,
    mailboxId: string | undefined,
    search = '',
    collapseThreads = false,
    folders: Mailbox[] = [],
    sort: Sort = DEFAULT_SORT,
): MailboxList {
    const [total, setTotal] = useState(0);
    const [ids, setIds] = useState<(string | null)[]>([]);
    const [emails, setEmails] = useState<Record<string, Email>>({});
    const [loading, setLoading] = useState(false);
    const [answered, setAnswered] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [ignored, setIgnored] = useState<string[]>([]);

    const key = `${accountId ?? ''}:${mailboxId ?? ''}:${search.trim()}:${collapseThreads ? 'th' : 'flat'}:${sort.field}:${sort.ascending}:${folders.length}`;
    const keyRef = useRef(key);
    // Held in refs so loadPage's identity depends on `key` alone. A caller
    // passing a fresh array or object each render would otherwise change the
    // callback every time, and the effect that calls it would never settle.
    const foldersRef = useRef(folders);
    foldersRef.current = folders;
    const sortRef = useRef(sort);
    sortRef.current = sort;
    const pages = useRef(new Set<number>());
    const emailsRef = useRef<Record<string, Email>>({});
    emailsRef.current = emails;

    const loadPage = useCallback(
        async (page: number) => {
            const compiled = compileSearch(search, mailboxId, foldersRef.current);
            if (!client || !accountId) return;
            if (!compiled.filter) {
                /*
                    Nothing to ask. With a folder open or a search typed that is an answer — the
                    list is empty. With neither it is the moment after signing in, before the page
                    has picked the folder to land on, and saying "empty" there is the very thing
                    the flag exists to stop (measured: it was the first of the two false empties).
                */
                if (mailboxId || search.trim()) setAnswered(keyRef.current);
                return;
            }
            const forKey = keyRef.current;
            if (pages.current.has(page)) return;
            pages.current.add(page);
            setLoading(true);
            try {
                let filter = compiled.filter;
                const dropped = [...compiled.ignored];
                let q;

                // Retry rather than fail: a server that hasn't implemented one
                // condition names it, so that term is dropped and reported and
                // the rest of the search still runs.
                for (let attempt = 0; ; attempt++) {
                    try {
                        q = await client.queryEmails({
                            accountId,
                            filter,
                            position: page * PAGE,
                            limit: PAGE,
                            collapseThreads,
                            sort: sortRef.current,
                        });
                        break;
                    } catch (e) {
                        const unsupported =
                            e instanceof MailError && e.type === 'unsupportedFilter' ? e.message : null;
                        if (!unsupported || attempt >= 3) throw e;
                        const reduced = without(filter, unsupported);
                        if (!reduced) throw e;
                        filter = reduced;
                        dropped.push(operatorFor(unsupported) || unsupported);
                        pages.current.delete(page);
                    }
                }

                if (keyRef.current !== forKey) return;
                setIgnored(dropped);

                const knownTotal = q.total ?? page * PAGE + q.ids.length;
                setTotal((t) => Math.max(t, knownTotal));
                setIds((prev) => {
                    const next = prev.slice();
                    if (next.length < knownTotal) next.length = knownTotal;
                    q.ids.forEach((id, i) => {
                        next[q.position + i] = id;
                    });
                    return next;
                });

                const missing = q.ids.filter((id) => !emailsRef.current[id]);
                if (missing.length) {
                    const list = await client.getEmailHeaders(accountId, missing);
                    if (keyRef.current !== forKey) return;
                    setEmails((prev) => {
                        const next = { ...prev };
                        for (const e of list) next[e.id] = e;
                        return next;
                    });
                }
                setError(null);
            } catch (e) {
                pages.current.delete(page);
                if (keyRef.current === forKey) setError(e instanceof Error ? e.message : String(e));
            } finally {
                if (keyRef.current === forKey) {
                    setLoading(false);
                    setAnswered(forKey);
                }
            }
        },
        /*
            `key` is here on purpose and the rule cannot see it.

            The body reads keyRef.current rather than key, so the rule calls the dependency
            unnecessary — but key carries the sort and the folder count, which nothing else in this
            list does, and dropping it would leave the callback's identity unchanged when the sort
            changes. The effect below re-fetches on exactly that identity.

            The obvious way to satisfy the rule is to capture key instead of reading the ref. That
            was considered and rejected: the ref lags one render behind on purpose, and the
            in-flight cancellation checks are written against that lag. Rewriting the staleness
            rules of a paging hook to quiet a linter is how a stale-closure fix grows a new one.
        */
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [client, accountId, mailboxId, search, collapseThreads, key],
    );

    // Switching mailbox drops everything: ids are positional and not comparable across mailboxes.
    useEffect(() => {
        keyRef.current = key;
        pages.current = new Set();
        setIds([]);
        setTotal(0);
        setError(null);
        if (accountId && client) void loadPage(0);
    }, [key, accountId, client, loadPage]);

    const ensureRange = useCallback(
        (start: number, end: number) => {
            const first = Math.max(0, Math.floor(start / PAGE));
            const last = Math.floor(Math.max(start, end) / PAGE);
            for (let p = first; p <= last; p++) void loadPage(p);
        },
        [loadPage],
    );

    const reload = useCallback(() => {
        pages.current = new Set();
        setIds([]);
        setTotal(0);
        void loadPage(0);
    }, [loadPage]);

    const patch = useCallback((id: string, changes: Partial<Email>) => {
        setEmails((prev) => {
            const current = prev[id];
            if (!current) return prev;
            return { ...prev, [id]: { ...current, ...changes } };
        });
    }, []);

    /** Removes a message that has left this mailbox, closing the gap it leaves behind. */
    const drop = useCallback((id: string) => {
        setIds((prev) => {
            const at = prev.indexOf(id);
            if (at === -1) return prev;
            const next = prev.slice();
            next.splice(at, 1);
            return next;
        });
        setTotal((t) => Math.max(0, t - 1));
    }, []);

    return { total, ids, emails, loading, loaded: answered === key, error, ignored, ensureRange, reload, patch, drop };
}
